import React, { memo, useState, useEffect } from 'react';
import { Accordion } from './Accordion';

interface Category {
    id: number;
    name: string;
    alias: string;
}

interface PageType {
    id: number;
    value: string;
}

interface SearchFiltersProps {
    selectedCategories: string[];
    selectedPageTypes: string[];
    onCategoryFilter: (category: string, checked: boolean) => void;
    onPageTypeFilter: (pageType: string, checked: boolean) => void;
    onResetFilters: () => void;
}

export const SearchFilters: React.FC<SearchFiltersProps> = memo(({
    selectedCategories,
    selectedPageTypes,
    onCategoryFilter,
    onPageTypeFilter,
    onResetFilters
}) => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [pageTypes, setPageTypes] = useState<PageType[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                
                // Fetch categories and page types in parallel
                const [categoriesResponse, pageTypesResponse] = await Promise.all([
                    fetch('/umbraco/api/CategoriesApi/GetCategories'),
                    fetch('/umbraco/api/CategoriesApi/GetPageTypes')
                ]);

                if (!categoriesResponse.ok) {
                    throw new Error('Failed to fetch categories');
                }

                if (!pageTypesResponse.ok) {
                    throw new Error('Failed to fetch page types');
                }

                const categoriesData = await categoriesResponse.json();
                const pageTypesData = await pageTypesResponse.json();
                
                setCategories(categoriesData.categories || []);
                setPageTypes(pageTypesData.pageTypes || []);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred');
                console.error('Error fetching filter data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const renderCategoriesContent = () => {
        if (loading) {
            return (
                <div className="filter-options">
                    <p>Loading categories...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className="filter-options">
                    <p>Error loading categories: {error}</p>
                </div>
            );
        }

        if (categories.length === 0) {
            return (
                <div className="filter-options">
                    <p>No categories available</p>
                </div>
            );
        }       

        return (
            <div className="filter-options">
                {categories.map((category) => (
                    <label key={category.id} className="filter-checkbox">
                        <input 
                            type="checkbox" 
                            checked={selectedCategories.includes(category.name)}
                            onChange={(e) => onCategoryFilter(category.name, e.target.checked)}
                        />
                        <span>{category.name}</span>
                    </label>
                ))}
            </div> 
        );
    };

    const renderPageTypesContent = () => {
        if (loading) {
            return (
                <div className="filter-options">
                    <p>Loading page types...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className="filter-options">
                    <p>Error loading page types: {error}</p>
                </div>
            );
        }

        if (pageTypes.length === 0) {
            return (
                <div className="filter-options">
                    <p>No page types available</p>
                </div>
            );
        }        
        
        return (
            <div className="filter-options">
                {pageTypes.map((pageType) => (
                    <label key={pageType.id} className="filter-checkbox">
                        <input 
                            type="checkbox" 
                            checked={selectedPageTypes.includes(pageType.value)}
                            onChange={(e) => onPageTypeFilter(pageType.value, e.target.checked)}
                        />
                        <span>{pageType.value}</span>
                    </label>
                ))}
            </div>
        );
    };

    return (
        <div className='search-page-filters-container'>              
            <div className='search-page-filters desktop'>
                <div className='search-page-filters-header'>
                    <p className='h5-style'>Filters</p>
                    <a className='green-hyperlink' onClick={onResetFilters} style={{ cursor: 'pointer' }}>Reset Filters</a>
                </div>                                        
                <div className='search-page-filters-accordion'>
                    <Accordion
                        items={[
                            {
                                id: 'page-type',
                                title: 'Page Type',
                                content: renderPageTypesContent()
                            },
                            {
                                id: 'categories',
                                title: 'Categories',
                                content: renderCategoriesContent()
                            }
                        ]}
                        oneOpenAtATime={false}
                        allOpenByDefault={true}
                    />
                </div>
            </div>
            <Accordion
                className='search-page-filters mobile'
                items={[
                    {
                        id: 'filters',
                        title: 'Filters',
                        content: (                            
                            <div className='search-page-filters'>
                                <div className='search-page-filters-header'>
                                    <p className='h5-style'>Filters</p>
                                    <a className='green-hyperlink' onClick={onResetFilters} style={{ cursor: 'pointer' }}>Reset Filters</a>
                                </div>                                          
                                <div className='search-page-filters-accordion'>
                                    <Accordion
                                        items={[
                                            {
                                                id: 'page-type',
                                                title: 'Page Type',
                                                content: renderPageTypesContent()
                                            },
                                            {
                                                id: 'categories',
                                                title: 'Categories',
                                                content: renderCategoriesContent()
                                            }
                                        ]}
                                        oneOpenAtATime={false}
                                        allOpenByDefault={true}
                                    />
                                </div>
                            </div>
                        )
                    }]}
                    oneOpenAtATime={false}
                    allOpenByDefault={false}                
                />        
        </div>
    );
});

SearchFilters.displayName = 'SearchFilters';
